import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { reportSchoolAPI, authorizationAPI } from '../../services/api';
import UniformTables from './UniformTables';
import './ReportSchool.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

const ReportSchool = () => {
  const { t } = useTranslation(['reports', 'common']);
  const { user } = useAuth();

  // Filter states
  const [ppds, setPPDs] = useState([]);
  const [schools, setSchools] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedPPD, setSelectedPPD] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');
  const [activityStatus, setActivityStatus] = useState('');

  // Report data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal states
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [gradeStatuses, setGradeStatuses] = useState([]);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Permission states - fetched from API
  const [resourcePermissions, setResourcePermissions] = useState(null);

  // Load PPDs and permissions on component mount
  useEffect(() => {
    loadPPDs();
    loadResourcePermissions();
  }, []);

  // Function to load user's permissions for this resource from API
  const loadResourcePermissions = async () => {
    try {
      const resourceCode = '/report-school';
      const data = await authorizationAPI.getPermissions(resourceCode);

      console.log('[ReportSchool] Raw permission response:', data);

      if (data.success && data.permissions) {
        const permissions = {
          resourceCode: data.resourceCode,
          hasAccess: data.permissions.hasAccess,
          dataScope: data.permissions.dataScope,
          submit: data.permissions.canSubmit,
          reopenSubmission: data.permissions.canReopenSubmission,
          manageSchool: data.permissions.canManageSchool
        };
        console.log('[ReportSchool] Mapped permissions:', permissions);
        setResourcePermissions(permissions);
      } else {
        console.error('[ReportSchool] Invalid permission response format');
        setResourcePermissions(null);
      }
    } catch (error) {
      console.error('[ReportSchool] Error loading permissions:', error);
      setResourcePermissions(null);
    }
  };

  // Load schools when PPD changes
  useEffect(() => {
    if (selectedPPD) {
      loadSchools(selectedPPD);
    } else {
      setSchools([]);
      setSelectedSchool('');
    }
  }, [selectedPPD]);

  // Load activities when school changes
  useEffect(() => {
    if (selectedSchool) {
      loadActivities(selectedSchool);
    } else {
      setActivities([]);
      setSelectedActivity('');
      setActivityStatus('');
    }
  }, [selectedSchool]);

  // Load report data when school and activity are selected
  useEffect(() => {
    if (selectedSchool && selectedActivity) {
      loadReportData();
    } else {
      setReportData(null);
    }
  }, [selectedSchool, selectedActivity]);

  const loadPPDs = async () => {
    try {
      const response = await reportSchoolAPI.getPPDs();
      if (response.success) {
        setPPDs(response.data);

        // For TEACHER/HEADMASTER, auto-select the only PPD
        if (response.data.length === 1) {
          setSelectedPPD(response.data[0].ppd);
        }
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to load PPDs:', error);
      setError(t('reports:school.messages.failedToLoad') + ' districts');
    }
  };

  const loadSchools = async (ppdCode) => {
    try {
      const response = await reportSchoolAPI.getSchools(ppdCode);
      if (response.success) {
        setSchools(response.data);

        // For TEACHER/HEADMASTER, auto-select the only school
        if (response.data.length === 1) {
          setSelectedSchool(response.data[0].code);
        }
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to load schools:', error);
      setError(t('reports:school.messages.failedToLoad') + ' schools');
    }
  };

  const loadActivities = async (schoolCode) => {
    try {
      const response = await reportSchoolAPI.getActivities(schoolCode);
      if (response.success) {
        console.log('[ReportSchool] Activities loaded:', response.data);
        setActivities(response.data);

        // Auto-select the first activity if only one exists
        if (response.data.length === 1) {
          setSelectedActivity(response.data[0].activityId.toString());
          setActivityStatus(response.data[0].status);
          console.log('[ReportSchool] Auto-selected activity:', {
            activityId: response.data[0].activityId,
            status: response.data[0].status
          });
        }
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to load activities:', error);
      setError(t('reports:school.messages.failedToLoad') + ' activities');
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportSchoolAPI.getReportData(selectedSchool, selectedActivity);
      if (response.success) {
        setReportData(response.data);
      } else {
        setError(response.message || t('reports:school.messages.failedToLoad') + ' report data');
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to load report data:', error);
      setError(t('reports:school.messages.failedToLoad') + ' report data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSchool = async () => {
    try {
      const response = await reportSchoolAPI.submitSchool({
        schoolCode: selectedSchool,
        activityId: parseInt(selectedActivity)
      });

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('reports:school.messages.submitSuccess'),
          text: t('reports:school.messages.submitSuccessMessage'),
          confirmButtonColor: '#28a745'
        });
        setShowSubmitModal(false);
        loadReportData(); // Reload to get updated status
      } else {
        Swal.fire({
          icon: 'error',
          title: t('reports:school.messages.submitFailed'),
          text: response.message || t('reports:school.messages.submitFailedMessage'),
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to submit school:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:school.messages.error'),
        text: t('reports:school.messages.failedToSubmit') + ' ' + (error.response?.data?.message || error.message),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleReopenGrades = async (gradesToReopen) => {
    try {
      const response = await reportSchoolAPI.updateGradeStatus({
        schoolCode: selectedSchool,
        activityId: parseInt(selectedActivity),
        gradeUpdates: gradesToReopen
      });

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('reports:school.messages.updateSuccess'),
          text: t('reports:school.messages.updateSuccessMessage'),
          confirmButtonColor: '#28a745'
        });
        setShowReopenModal(false);
        loadReportData(); // Reload to get updated status
      } else {
        Swal.fire({
          icon: 'error',
          title: t('reports:school.messages.updateFailed'),
          text: response.message || t('reports:school.messages.updateFailedMessage'),
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to update grades:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:school.messages.error'),
        text: t('reports:school.messages.failedToUpdate') + ' ' + (error.response?.data?.message || error.message),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Helper function to check if user has permission for an action
  // Checks from API permissions instead of hardcoded matrix
  const hasPermission = (action) => {
    if (!user || !user.role) {
      console.log('[ReportSchool] hasPermission: No user or role');
      return false;
    }

    // If we have resource permissions from API, use them
    if (resourcePermissions) {
      let result = false;
      if (action === 'Submit') {
        result = resourcePermissions.submit === true;
      } else if (action === 'ReopenSubmission') {
        result = resourcePermissions.reopenSubmission === true;
      }
      console.log(`[ReportSchool] hasPermission(${action}): Using API permissions = ${result}`, {
        resourcePermissions
      });
      return result;
    }

    // Fallback to default permissions if API data not loaded yet
    const role = user.role.toUpperCase();
    const defaultPermissions = {
      'TEACHER': { submit: false, reopenSubmission: false },
      'HEADMASTER': { submit: true, reopenSubmission: true },
      'PPD': { submit: false, reopenSubmission: true },
      'ADMIN': { submit: true, reopenSubmission: true },
      'SUPERADMIN': { submit: true, reopenSubmission: true }
    };

    const userPermissions = defaultPermissions[role];
    if (!userPermissions) {
      console.log(`[ReportSchool] hasPermission(${action}): Unknown role ${role}`);
      return false;
    }

    let result = false;
    if (action === 'Submit') result = userPermissions.submit;
    else if (action === 'ReopenSubmission') result = userPermissions.reopenSubmission;

    console.log(`[ReportSchool] hasPermission(${action}): Using default permissions for ${role} = ${result}`);
    return result;
  };

  const handleActivityChange = (e) => {
    const activityId = e.target.value;
    setSelectedActivity(activityId);

    console.log('[ReportSchool] Activity changed:', {
      selectedActivityId: activityId,
      activitiesCount: activities.length,
      activitiesList: activities.map(a => ({ id: a.activityId, status: a.status }))
    });

    // Find the selected activity and set its status
    if (activityId) {
      const selectedActivityObj = activities.find(a => a.activityId.toString() === activityId);
      console.log('[ReportSchool] Found activity object:', selectedActivityObj);
      if (selectedActivityObj) {
        setActivityStatus(selectedActivityObj.status);
        console.log('[ReportSchool] Set activityStatus to:', selectedActivityObj.status);
      } else {
        console.warn('[ReportSchool] Activity not found in activities array!');
      }
    } else {
      setActivityStatus('');
      console.log('[ReportSchool] Cleared activityStatus');
    }
  };

  const openReopenModal = async () => {
    try {
      const response = await reportSchoolAPI.getGradeStatus(selectedSchool, selectedActivity);
      if (response.success) {
        setGradeStatuses(response.data);
        setShowReopenModal(true);
      }
    } catch (error) {
      console.error('[ReportSchool] Failed to load grade statuses:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:school.messages.error'),
        text: t('reports:school.messages.failedToLoad') + ' grade statuses',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportToExcel = async () => {
    // Validation
    if (!selectedSchool || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:school.messages.selectSchoolActivity'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await reportSchoolAPI.exportToExcel({
        SchoolCode: selectedSchool,
        ActivityId: parseInt(selectedActivity),
        SplitBy: 0,
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.excelDownloaded'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.response?.data?.message || error.message || 'Unknown error',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportToPDF = async () => {
    if (!reportData) {
      Swal.fire({
        icon: 'info',
        title: t('reports:common.messages.noDataToExport'),
        text: t('reports:common.messages.noDataToExport'),
        confirmButtonColor: '#17a2b8'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.exporting'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Server-side PDF export with QuestPDF
      await reportSchoolAPI.exportToPdf({
        schoolCode: selectedSchool,
        activityId: selectedActivity,
        splitBy: 0, // None
        includeSubtotals: true,
        includeGrandTotal: true,
        showPageHeaders: true,
        applyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('[ReportSchool] Failed to export PDF:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      // DO NOT clear diagnostic data - we want to see the user's activity trail

      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  // Determine if dropdowns should be disabled based on role
  const isPPDDisabled = ppds.length === 1; // Disable if only one option
  const isSchoolDisabled = schools.length === 1; // Disable if only one option

  return (
    <div className="report-school">
      {/* Error Display */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('reports:school.messages.loadingData')}</p>
        </div>
      )}

      {/* Container - matches old WebForms layout */}
      <div className="container">
        {/* Header with Logo and Title */}
        <div className="header-container">
          <img className="logo" src="/KTC.png" alt="KTC" />
          <h2>{t('reports:school.title')} ({t('reports:school.titleEn')})</h2>
          <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
            <i className="fas fa-bug"></i>
          </button>
        </div>

        {/* Info Section - 3 columns like old layout */}
        <div className="info-section">
          {/* Left Column */}
          <div className="info-column">
            <div className="info-row">
              <label htmlFor="daerah"><strong>{t('reports:school.filters.district')}</strong></label>
              <select
                id="daerah"
                value={selectedPPD}
                onChange={(e) => setSelectedPPD(e.target.value)}
                disabled={isPPDDisabled}
              >
                <option value="">{t('reports:school.filters.selectPPD')}</option>
                {ppds.map(ppd => (
                  <option key={ppd.ppd} value={ppd.ppd}>{ppd.ppd}</option>
                ))}
              </select>
            </div>
            <div className="info-row">
              <p><strong>{t('reports:school.filters.schoolCode')}</strong> {reportData?.schoolCode || ''}</p>
            </div>
            <div className="info-row">
              <label htmlFor="sekolah"><strong>{t('reports:school.filters.school')}</strong></label>
              <select
                id="sekolah"
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                disabled={isSchoolDisabled || !selectedPPD}
              >
                <option value="">{t('reports:school.filters.selectSchool')}</option>
                {schools.map(school => (
                  <option key={school.code} value={school.code}>
                    {school.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="info-row">
              <label htmlFor="activity"><strong>{t('reports:school.filters.activity')}</strong></label>
              <select
                id="activity"
                value={selectedActivity}
                onChange={handleActivityChange}
                disabled={!selectedSchool}
              >
                <option value="">{t('reports:school.filters.selectActivity')}</option>
                {activities.map(activity => (
                  <option key={activity.activityId} value={activity.activityId}>
                    {activity.activityDisplay}
                  </option>
                ))}
              </select>
            </div>
            {reportData && (
              <>
                <div className="info-row">
                  <p><strong>{t('reports:school.filters.dun')}</strong> {reportData.dunName} ({reportData.dunKod})</p>
                </div>
                <div className="info-row">
                  <p><strong>{t('reports:school.filters.parliament')}</strong> {reportData.parName} ({reportData.parKod})</p>
                </div>
              </>
            )}
          </div>

          {/* Middle Column - Totals */}
          <div className="info-column">
            {reportData && (
              <>
                <div className="info-row">
                  <p><strong>{t('reports:school.stats.male')}</strong> {reportData.totalLelaki?.toLocaleString()}</p>
                </div>
                <div className="info-row">
                  <p><strong>{t('reports:school.stats.female')}</strong> {reportData.totalPerempuan?.toLocaleString()}</p>
                </div>
                <div className="info-row">
                  <p><strong>{t('reports:school.stats.total')}</strong> {reportData.totalJumlah?.toLocaleString()}</p>
                </div>
              </>
            )}
          </div>

          {/* Right Column - Report Info */}
          <div className="info-column">
            <div className="info-row">
              <p><strong>{t('reports:school.stats.date')}</strong> {new Date().toLocaleDateString('en-GB').replace(/\//g, '.')}</p>
            </div>
            {reportData && (
              <div className="info-row">
                <p><strong>{t('reports:school.stats.status')}</strong> {reportData.schoolStatus}</p>
              </div>
            )}
          </div>
        </div>

        {reportData && (
          <>
            {/* Action Buttons */}
            <div className="action-buttons-container">
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="action-btn btn-excel" onClick={handleExportToExcel}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z"/>
                  </svg>
                  {t('reports:school.buttons.exportExcel')}
                </button>
                <button type="button" className="action-btn btn-pdf" onClick={handleExportToPDF}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z"/>
                  </svg>
                  {t('reports:school.buttons.exportPDF')}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {reportData.schoolStatus === 'OPEN' && (() => {
                  // Check if all grades are CLOSED
                  const allGradesClosed = reportData.grades && reportData.grades.length > 0 &&
                    reportData.grades.every(grade => grade.status && grade.status.toUpperCase() === 'CLOSED');

                  // Debug logging
                  const hasSubmitPermission = hasPermission('Submit');
                  const isActivityActive = activityStatus?.toUpperCase() === 'ACTIVE';
                  const isButtonDisabled = !hasSubmitPermission || !isActivityActive || !allGradesClosed;

                  console.log('[ReportSchool] Hantar Button State:', {
                    hasSubmitPermission,
                    activityStatus,
                    isActivityActive,
                    allGradesClosed,
                    gradeStatuses: reportData.grades?.map(g => ({ tahun: g.tahun, status: g.status })),
                    isButtonDisabled
                  });

                  return (
                    <button
                      type="button"
                      className="action-btn btn-submit"
                      onClick={() => setShowSubmitModal(true)}
                      disabled={isButtonDisabled}
                    >
                      {t('reports:school.buttons.submit')}
                    </button>
                  );
                })()}
                <button
                  type="button"
                  className="action-btn btn-reopen"
                  onClick={openReopenModal}
                  disabled={reportData.schoolStatus === 'COMPLETED' || !hasPermission('ReopenSubmission') || activityStatus?.toUpperCase() !== 'ACTIVE'}
                >
                  {t('reports:school.buttons.reopenSubmission')}
                </button>
              </div>
            </div>

          {/* Report Tables */}
          <div className="report-tables">
            {/* Uniform Tables - pass ALL grades to show in one table */}
            <UniformTables grades={reportData.grades} />
          </div>

          {/* Grand Totals */}
          <div className="grand-totals">
            <h3>{t('reports:school.grandTotal')}</h3>
            <div className="totals-row">
              <span className="total-item">
                <strong>{t('reports:school.totalMale')}</strong> {reportData.totalLelaki}
              </span>
              <span className="total-item">
                <strong>{t('reports:school.totalFemale')}</strong> {reportData.totalPerempuan}
              </span>
              <span className="total-item">
                <strong>{t('reports:school.totalStudents')}</strong> {reportData.totalJumlah}
              </span>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="modal-overlay" onClick={() => setShowSubmitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('reports:school.modal.confirmSubmit')}</h3>
            <p>{t('reports:school.modal.confirmSubmitMessage')}</p>
            <p className="text-warning">
              <i className="fas fa-exclamation-triangle"></i>
              {t('reports:school.modal.submitWarning')}
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowSubmitModal(false)}>
                {t('reports:school.modal.cancel')}
              </button>
              <button className="btn btn-success" onClick={handleSubmitSchool}>
                {t('reports:school.modal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-Open Modal */}
      {showReopenModal && (
        <div className="modal-overlay" onClick={() => setShowReopenModal(false)}>
          <div className="modal-content reopen-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('reports:school.modal.reopenTitle')}</h3>
            <div className="status-table-container">
              <table className="status-table">
                <thead>
                  <tr>
                    <th>{t('reports:school.modal.reopenTableHeaders.year')}</th>
                    <th>{t('reports:school.modal.reopenTableHeaders.lastUpdatedBy')}</th>
                    <th>{t('reports:school.modal.reopenTableHeaders.lastUpdatedAt')}</th>
                    <th>{t('reports:school.modal.reopenTableHeaders.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeStatuses.map((status, index) => (
                    <tr key={status.tahun}>
                      <td>{status.tahunText}</td>
                      <td>{status.lastUpdatedBy || '-'}</td>
                      <td>{status.lastUpdatedAt || '-'}</td>
                      <td>
                        <select
                          id={`status-${index}`}
                          className="status-select"
                          defaultValue={status.status}
                        >
                          <option value="OPEN">OPEN</option>
                          <option value="CLOSED">CLOSED</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowReopenModal(false)}>
                {t('reports:school.modal.close')}
              </button>
              <button
                className="btn btn-warning"
                onClick={() => {
                  const updatedStatuses = gradeStatuses.map((status, index) => {
                    const select = document.getElementById(`status-${index}`);
                    return {
                      tahun: status.tahun,
                      status: select.value
                    };
                  });
                  handleReopenGrades(updatedStatuses);
                }}
              >
                {t('reports:school.modal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report School',
            selectedPPD,
            selectedSchool,
            selectedActivity,
            activityStatus,
            reportData
          }}
        />
      )}
    </div>
  );
};

export default ReportSchool;
